import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Globe, Lock } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { api, mediaUrl } from "../lib/api";
import PinModal from "./PinModal";
import CookieBanner from "./CookieBanner";

const NAV = [
  { to: "/", key: "nav_home" },
  { to: "/operador-digital", key: "nav_operator" },
  { to: "/ecosistema", key: "nav_ecosystem" },
  { to: "/legal", key: "nav_legal" },
  { to: "/contacto", key: "nav_contact" },
];

export default function PublicLayout() {
  const { t, lang, setLang, tr } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [cookieReopen, setCookieReopen] = useState(0);
  const [legalLinks, setLegalLinks] = useState([]);
  const location = useLocation();

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  useEffect(() => {
    api.get("/public/legal/menu").then((r) => {
      const docs = r.data.flatMap((c) => c.documents || []);
      const wanted = ["condiciones-generales", "politica-de-privacidad", "politica-de-cookies"];
      setLegalLinks(docs.filter((d) => wanted.includes(d.slug)));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-[#0B1220]">
      <header className="fixed top-0 inset-x-0 z-50 glass" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[132px]">
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <img src={mediaUrl("/api/uploads/naturlogo-v2.png")} alt="Natur Commodities" className="h-[113px] w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-link-${n.key}`}
                className={({ isActive }) => `text-sm transition-colors duration-200 ${isActive ? "text-[#5698C7]" : "text-slate-500 hover:text-[#0B1220]"}`}>
                {t(n.key)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={() => setLang(lang === "es" ? "en" : "es")} data-testid="lang-toggle-btn"
              className="flex items-center gap-1 text-xs font-mono-tech text-slate-500 hover:text-[#5698C7] transition-colors duration-200 border border-slate-200 px-2 py-1.5">
              <Globe size={12} strokeWidth={1.5} /> {lang.toUpperCase()}
            </button>
            {user ? (
              <Link to={user.role === "admin" ? "/matriz" : "/intranet"} data-testid="header-intranet-btn"
                className="hidden sm:flex items-center gap-2 bg-[#5698C7] text-white text-xs font-mono-tech font-medium tracking-widest px-4 py-2 hover:bg-[#0B1220] transition-colors duration-200">
                <Lock size={12} strokeWidth={2} /> {t("nav_intranet").toUpperCase()}
              </Link>
            ) : (
              <button onClick={() => setShowPin(true)} data-testid="header-intranet-btn"
                className="hidden sm:flex items-center gap-2 bg-[#5698C7] text-white text-xs font-mono-tech font-medium tracking-widest px-4 py-2 hover:bg-[#0B1220] transition-colors duration-200">
                <Lock size={12} strokeWidth={2} /> {t("nav_intranet").toUpperCase()}
              </button>
            )}
            <button className="lg:hidden text-[#0B1220]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-200 px-6 py-4 space-y-3" data-testid="mobile-menu">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className="block text-sm text-slate-600 hover:text-[#5698C7]">{t(n.key)}</NavLink>
            ))}
            <button onClick={() => { setOpen(false); setShowPin(true); }} className="block text-sm text-[#5698C7]" data-testid="mobile-intranet-btn">{t("nav_intranet")}</button>
          </div>
        )}
      </header>
      {showPin && <PinModal onClose={() => setShowPin(false)} />}

      <main className="pt-[132px]"><Outlet /></main>

      <footer className="border-t border-slate-200 bg-white" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="flex flex-col items-center">
            <Link to="/" data-testid="footer-logo-link" className="block">
              <img src={mediaUrl("/api/uploads/naturlogo-v2.png")} alt="Natur Commodities" className="h-[216px] w-auto mx-auto mb-5" data-testid="footer-logo" />
            </Link>
            <p className="text-xs text-slate-500 leading-relaxed max-w-xs text-justify">{t("operator_sub")}</p>
          </div>
          <div className="flex gap-8 items-center">
            <span className="w-[3px] h-[125px] bg-[#C9C9C9] rounded-full" aria-hidden="true" />
            <div className="h-[150px]">
              <div className="font-mono-tech text-xs tracking-[0.25em] font-semibold text-[#0B1220] mb-5">{t("footer_legal_title").toUpperCase()}</div>
              <ul className="space-y-2">
              {legalLinks.map((d) => (
                <li key={d.slug}>
                  <Link to={`/legal/${d.slug}`} className="text-xs text-slate-500 hover:text-[#5698C7] transition-colors duration-200" data-testid={`footer-legal-${d.slug}`}>{tr(d.title)}</Link>
                </li>
              ))}
              <li><Link to="/legal" className="text-xs text-slate-500 hover:text-[#5698C7] transition-colors duration-200" data-testid="footer-legal-area">{t("nav_legal")}</Link></li>
              <li><button onClick={() => setCookieReopen((c) => c + 1)} className="text-xs text-slate-500 hover:text-[#5698C7] transition-colors duration-200" data-testid="footer-cookie-prefs-btn">{t("footer_cookie_prefs")}</button></li>
            </ul>
            </div>
          </div>
          <div className="flex gap-8 items-center">
            <span className="w-[3px] h-[125px] bg-[#C9C9C9] rounded-full" aria-hidden="true" />
            <div className="h-[150px]">
              <div className="font-mono-tech text-xs tracking-[0.25em] font-semibold text-[#0B1220] mb-5">{t("footer_system_title").toUpperCase()}</div>
              <button onClick={() => setShowPin(true)} className="text-xs text-slate-500 hover:text-[#5698C7] transition-colors duration-200" data-testid="footer-intranet-btn">{t("nav_intranet")}</button>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-100 py-4 px-4 text-center" data-testid="footer-security-notice">
          <p className="text-[12px] sm:text-[13px] font-bold text-red-600 leading-relaxed max-w-5xl mx-auto">
            Recuerda que <u>NUNCA</u> se te solicitará DATOS BANCARIOS, TARJETAS de Crédito, ni DATOS sensibles vía email/correos electrónicos, ni por teléfono.
            Esos datos son únicamente ingresados por ti en el AREA USUARIO o en tu área personal de tu Blog o Tienda favorita dentro de los dominios de
            Natur Commodities SL.
          </p>
        </div>
        <div className="border-t border-slate-100 py-4 text-center">
          <span className="font-mono-tech text-[10px] text-slate-400 tracking-widest">© {new Date().getFullYear()} NATUR COMMODITIES · {t("footer_rights")}</span>
        </div>
      </footer>
      <CookieBanner reopen={cookieReopen} />
    </div>
  );
}
